import {inject, injectable} from 'inversify';
import {BookingKitApiFactoryInterface} from "@/models/Factories/BookingKitApiFactoryInterface";
import {BOOKINGKIT_API_FACTORY, SUPPLIER_STATE_SERVICE} from "@/bootstrap/ServiceProviders";
import {UseSupplierInterface} from "@/state/supplier";
import {
    TicketHolder,
    TicketHolderPhoto,
    TicketHolderRequest
} from "@/services/TicketHolderService/types";
import {fromApiToTicketHolder} from "@/services/TicketHolderService/Reducers";
import TicketHolderServiceError from "./Errors/TicketHolderServiceError";
import type {UpdateVendorTicketHolderRequest} from "@bookingkit/api-v4/models";
import {format} from "date-fns";
import {CreateVendorTicketHolderRequest} from "@bookingkit/api-v4";

@injectable()
export class TicketHolderService {
    private connection: ReturnType<BookingKitApiFactoryInterface['getConnection']>;

    private supplierId: string;

    constructor(
        @inject(BOOKINGKIT_API_FACTORY) factory: BookingKitApiFactoryInterface,
        @inject(SUPPLIER_STATE_SERVICE) useSupplierState: () => UseSupplierInterface,
    ) {
        const {supplier} = useSupplierState();
        this.supplierId = supplier.value!.id;
        this.connection = factory.getConnection();
        this.getTicketHolder = this.getTicketHolder.bind(this);
    }

    async getTicketHolder(getTicketHolderRequest: TicketHolderRequest): Promise<TicketHolder> {
        const { supplierId } = this;
        const {ticketHolderId} = getTicketHolderRequest;
        const { data } = await this.connection.getVendorTicketHolder({
            vendorId: supplierId,
            ticketHolderId: ticketHolderId,
        });
        if (!data) {
            throw TicketHolderServiceError.fromMissingProperty('data');
        }
        return fromApiToTicketHolder(data);
    }

    async postTicketHolderPhoto(args: {
        ticketHolderId: string;
        photo: File;
    }): Promise<void> {
        const { supplierId } = this;
        const {  ticketHolderId, photo } = args;
        const response = await this.connection.uploadVendorTicketHolderPhoto(
            {
                vendorId: supplierId,
                ticketHolderId,
                photo,
            }
        );

        if (!response) {
            throw TicketHolderServiceError.fromMissingProperty('data');
        }
    }

    async patchTicketHolder(
        ticketHolderId: string,
        ticketHolderPatchRequest: UpdateVendorTicketHolderRequest
    ): Promise<TicketHolder> {
        const { supplierId } = this;
        const {data} = await this.connection.updateVendorTicketHolder({
            vendorId: supplierId,
            ticketHolderId,
            updateVendorTicketHolderRequest : ticketHolderPatchRequest
        });
        if (!data) {
            throw TicketHolderServiceError.fromMissingProperty('data');
        }
        return fromApiToTicketHolder(data);
    }

    async createTicketHolder(
        ticketHolderData:  CreateVendorTicketHolderRequest
    ): Promise<TicketHolder> {
        const { supplierId } = this;
        const params = ticketHolderData as  CreateVendorTicketHolderRequest;
        if (ticketHolderData.dateOfBirth instanceof Date) {
            params.dateOfBirth = format(ticketHolderData.dateOfBirth, 'yyyy-MM-dd');
        }
        const { data } = await this.connection.createVendorTicketHolder(
            {
                vendorId: supplierId,
                createVendorTicketHolderRequest: params,
            }
        );
        if (!data) {
            throw TicketHolderServiceError.fromMissingProperty('data');
        }
        return fromApiToTicketHolder(data);
    }

    async getTicketHolderPhoto(args: { ticketHolderId: string }): Promise<string | undefined> {
        const request = {
            vendorId: this.supplierId,
            ticketHolderId: args.ticketHolderId,
        };
        const response = await this.connection.getVendorTicketHolderPhoto(request);
        return response?.data?.url;
    }

}


export default { TicketHolderService };
